import React, { useState, useEffect } from 'react';
import { axiosInstance } from '@/axiosConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  User, Lock, Building, Save, Palette, Upload, Users, Plus, Edit, Trash2, 
  CreditCard, Shield, AlertTriangle, Check, MessageSquare, ExternalLink
} from 'lucide-react';

// Funções utilitárias de máscara
const maskCPF = (v) => v.replace(/\D/g, '').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2').slice(0, 14);
const maskCNPJ = (v) => v.replace(/\D/g, '').replace(/^(\d{2})(\d)/, '$1.$2').replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3').replace(/\.(\d{3})(\d)/, '.$1/$2').replace(/(\d{4})(\d)/, '$1-$2').slice(0, 18);
const maskPhone = (v) => v.replace(/\D/g, '').replace(/^(\d{2})(\d)/g, '($1) $2').replace(/(\d)(\d{4})$/, '$1-$2').slice(0, 15);

export default function Perfil() {
  const [loading, setLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const userLogado = JSON.parse(localStorage.getItem('user') || '{}');

  // Estados dos Formulários
  const [formData, setFormData] = useState({
    nome: '', email: '', telefone: '', cpf: '',
    nome_agencia: '', cnpj_agencia: '', endereco: '', cidade_uf: '', pix: ''
  });
  const [prefData, setPrefData] = useState({
    cor_primaria: '#2563eb', texto_voucher: '', logo_url: ''
  });
  const [passData, setPassData] = useState({
    senha_atual: '', nova_senha: '', confirmar_senha: ''
  });

  // Estados de Dados
  const [teamMembers, setTeamMembers] = useState([]);
  const [usageData, setUsageData] = useState({ atual: 0, limite: 1 });
  const [subscriptionData, setSubscriptionData] = useState(null);

  // Estados dos Modais
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteData, setInviteData] = useState({ nome: '', email: '', role: 'vendedor' });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingData, setEditingData] = useState({ id: '', nome: '', email: '', telefone: '', role: 'vendedor' });

  useEffect(() => {
    loadPerfil();
  }, []);

  const loadPerfil = async () => {
    try {
      const response = await axiosInstance.get('/auth/me');
      const userData = response.data;
      
      const ehAdmin = userData.is_superuser || userData.role === 'admin';
      setIsAdmin(ehAdmin);

      if (ehAdmin) {
          loadTeamMembers();
          loadSubscription();
      }

      if (userData.agencia_dados) {
        setUsageData(prev => ({ ...prev, limite: userData.agencia_dados.limite_usuarios || 1 }));
      }

      if (userData.vendedor_id) {
          const vendResponse = await axiosInstance.get('/vendedores');
          const meuPerfil = vendResponse.data.find(v => v.id === userData.vendedor_id);
          
          if (meuPerfil) {
            setFormData({
                nome: meuPerfil.nome || '',
                email: meuPerfil.email || '',
                telefone: meuPerfil.telefone || '',
                cpf: meuPerfil.cpf || '',
                nome_agencia: userData.agencia_dados?.nome || meuPerfil.nome_agencia || '',
                cnpj_agencia: userData.agencia_dados?.cnpj || meuPerfil.cnpj_agencia || '',
                endereco: meuPerfil.endereco || '',
                cidade_uf: meuPerfil.cidade_uf || '',
                pix: meuPerfil.pix || ''
            });
            setPrefData({
                cor_primaria: userData.agencia_dados?.cor_primaria || '#2563eb',
                texto_voucher: userData.agencia_dados?.texto_voucher || '',
                logo_url: userData.agencia_dados?.logo_url || ''
            });
          }
      }
    } catch (error) {
      toast.error("Erro ao carregar perfil.");
    }
  };

  const loadTeamMembers = async () => {
      try {
          // Tenta carregar membros
          let data = [];
          try {
             const res = await axiosInstance.get('/team/members');
             data = res.data;
          } catch {
             // Fallback
             const res = await axiosInstance.get('/vendedores');
             data = res.data;
          }
          setTeamMembers(data || []);
          setUsageData(prev => ({ ...prev, atual: data ? data.length : 0 }));
      } catch (error) { console.error("Erro equipe", error); }
  };

  const loadSubscription = async () => {
      try {
          const res = await axiosInstance.get('/assinatura/status');
          setSubscriptionData(res.data);
      } catch (error) { console.error("Erro assinatura", error); }
  };

  // --- HANDLERS ---

  const handleProfileUpdate = async (e) => {
    e.preventDefault(); setLoading(true);
    try { 
        await axiosInstance.put('/auth/perfil', formData); 
        toast.success("Alterações salvas! Saia da conta e entre novamente para ver as mudanças."); 
    } catch (error) { 
        toast.error("Erro ao atualizar."); 
    } finally { setLoading(false); }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passData.nova_senha !== passData.confirmar_senha) return toast.error("As senhas não conferem.");
    setLoading(true);
    try {
        await axiosInstance.post('/auth/trocar-senha', { senha_atual: passData.senha_atual, nova_senha: passData.nova_senha });
        toast.success("Senha alterada com sucesso!");
        setPassData({ senha_atual: '', nova_senha: '', confirmar_senha: '' });
    } catch (error) { 
        toast.error("Erro ao trocar senha."); 
    } finally { setLoading(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    try {
        const res = await axiosInstance.post(`/vendedores/${userLogado.vendedor_id}/logo`, formDataUpload, { headers: { 'Content-Type': 'multipart/form-data' } });
        setPrefData(prev => ({ ...prev, logo_url: res.data.url }));
        toast.success("Logo atualizada!");
    } catch (error) { toast.error("Erro ao enviar logo."); }
  };

  const handlePrefUpdate = async (e) => { 
      e.preventDefault(); setLoading(true);
      try { 
        await axiosInstance.put('/auth/perfil', { cor_primaria: prefData.cor_primaria, texto_voucher: prefData.texto_voucher });
        toast.success("Preferências salvas!"); 
        const user = JSON.parse(localStorage.getItem('user'));
        if(user) {
            user.cor_primaria = prefData.cor_primaria;
            user.logo_url = prefData.logo_url;
            localStorage.setItem('user', JSON.stringify(user));
        }
      } catch(e) { toast.error("Erro ao salvar preferências"); } finally { setLoading(false); }
  };

  const handleInviteMember = async (e) => { 
      e.preventDefault(); setLoading(true);
      try { 
          await axiosInstance.post('/auth/invite', inviteData); 
          toast.success("Convite enviado!"); 
          setIsInviteModalOpen(false); 
          loadTeamMembers(); 
      } catch (e) { toast.error(e.response?.data?.detail || "Erro ao convidar"); } finally { setLoading(false); }
  };

  const handleSaveEdit = async (e) => {
      e.preventDefault(); setLoading(true);
      try {
          await axiosInstance.put(`/vendedores/${editingData.id}`, editingData);
          toast.success("Membro atualizado!"); setIsEditModalOpen(false); loadTeamMembers();
      } catch (error) { toast.error("Erro ao atualizar."); } finally { setLoading(false); }
  };

  const handleDeleteMember = async (member) => {
    if(!window.confirm(`Tem certeza que deseja remover ${member.nome}?`)) return;
    try { await axiosInstance.delete(`/vendedores/${member.vendedor_id || member.id}`); toast.success("Removido"); loadTeamMembers(); } catch(e) { toast.error("Erro"); }
  };

  const handleContactUpgrade = (planoAlvo) => {
    // Substitua pelo número real do suporte
    const telefoneSuporte = "5511999999999"; 
    
    // Nome da agência para a mensagem
    const nomeAgencia = formData.nome_agencia || 'Minha Agência';

    // Cria a mensagem personalizada
    const mensagem = `Olá! Sou da agência *${nomeAgencia}* e gostaria de solicitar uma alteração para o *${planoAlvo}*.`;
    
    // Link do WhatsApp
    const linkWhatsapp = `https://wa.me/${telefoneSuporte}?text=${encodeURIComponent(mensagem)}`;
    
    // Abre em nova aba
    window.open(linkWhatsapp, '_blank');
  };


  return (
    <div className="container mx-auto px-6 py-8 relative max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Meu Perfil</h1>
        <p className="text-gray-600">Gerencie suas informações pessoais e configurações.</p>
      </div>

      <Tabs defaultValue="dados" className="w-full">
        <TabsList className={`grid w-full mb-8 ${isAdmin ? 'grid-cols-4' : 'grid-cols-2'}`}>
          <TabsTrigger value="dados" className="flex items-center gap-2"><User className="w-4 h-4" /> Dados Pessoais</TabsTrigger>
          <TabsTrigger value="preferencias" className="flex items-center gap-2"><Palette className="w-4 h-4" /> Visual</TabsTrigger>
          {isAdmin && (
            <>
              <TabsTrigger value="equipe" className="flex items-center gap-2"><Users className="w-4 h-4" /> Equipe</TabsTrigger>
              <TabsTrigger value="assinatura" className="flex items-center gap-2"><CreditCard className="w-4 h-4" /> Assinatura</TabsTrigger>
            </>
          )}
        </TabsList>

        {/* --- DADOS --- */}
        <TabsContent value="dados" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Informações Gerais</CardTitle>
              <CardDescription>Dados da agência e contato pessoal.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProfileUpdate} className="space-y-6">
                
                {/* Dados Agência */}
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider flex items-center gap-2"><Building className="w-4 h-4" /> Dados da Agência</h3>
                        {!isAdmin && <Badge variant="outline" className="text-orange-600 bg-orange-50 border-orange-200">Leitura</Badge>}
                    </div>
                    <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${!isAdmin ? 'opacity-70 pointer-events-none' : ''}`}>
                        <div className="md:col-span-2"><Label>Razão Social / Nome Fantasia</Label><Input value={formData.nome_agencia} onChange={(e) => setFormData({...formData, nome_agencia: e.target.value})} /></div>
                        <div><Label>CNPJ</Label><Input value={formData.cnpj_agencia} onChange={(e) => setFormData({...formData, cnpj_agencia: maskCNPJ(e.target.value)})} maxLength={18} /></div>
                        <div><Label>Cidade / UF</Label><Input value={formData.cidade_uf} onChange={(e) => setFormData({...formData, cidade_uf: e.target.value})} /></div>
                        <div className="md:col-span-2"><Label>Endereço Completo</Label><Input value={formData.endereco} onChange={(e) => setFormData({...formData, endereco: e.target.value})} /></div>
                    </div>
                </div>

                <div className="border-t border-gray-100"></div>

                {/* Dados Usuário */}
                <div className="space-y-4">
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider flex items-center gap-2"><User className="w-4 h-4" /> Meus Dados</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2"><Label>Nome Completo</Label><Input value={formData.nome} onChange={(e) => setFormData({...formData, nome: e.target.value})} required /></div>
                        <div><Label>Email</Label><Input value={formData.email} disabled className="bg-gray-50" /></div>
                        <div><Label>Telefone / WhatsApp</Label><Input value={formData.telefone} onChange={(e) => setFormData({...formData, telefone: maskPhone(e.target.value)})} maxLength={15} /></div>
                        <div><Label>CPF</Label><Input value={formData.cpf} onChange={(e) => setFormData({...formData, cpf: maskCPF(e.target.value)})} maxLength={14} /></div>
                        <div><Label>Chave Pix (Comissões)</Label><Input value={formData.pix} onChange={(e) => setFormData({...formData, pix: e.target.value})} /></div>
                    </div>
                </div>

                <div className="flex justify-end pt-2">
                    <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white"><Save className="w-4 h-4 mr-2" /> Salvar Dados</Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* CARD SEGURANÇA */}
          <Card>
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <Lock className="w-5 h-5 text-gray-500" />
                        <div>
                            <CardTitle>Alterar Senha</CardTitle>
                            <CardDescription>Mantenha sua conta segura.</CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handlePasswordChange} className="max-w-xl space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                            <div className="md:col-span-1">
                                <Label>Senha Atual</Label>
                                <Input type="password" value={passData.senha_atual} onChange={(e) => setPassData({...passData, senha_atual: e.target.value})} required />
                            </div>
                            <div className="md:col-span-1">
                                <Label>Nova Senha</Label>
                                <Input type="password" value={passData.nova_senha} onChange={(e) => setPassData({...passData, nova_senha: e.target.value})} required />
                            </div>
                            <div className="md:col-span-1">
                                <Button type="submit" disabled={loading} variant="outline" className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">
                                    Atualizar Senha
                                </Button>
                            </div>
                        </div>
                        <div>
                             <Label className="text-xs text-gray-400">Confirmar Nova Senha</Label>
                             <Input type="password" value={passData.confirmar_senha} onChange={(e) => setPassData({...passData, confirmar_senha: e.target.value})} required className="mt-1" />
                        </div>
                    </form>
                </CardContent>
          </Card>
        </TabsContent>

        {/* --- PREFERÊNCIAS --- */}
        <TabsContent value="preferencias">
          <Card>
            <CardHeader><CardTitle>Identidade Visual</CardTitle><CardDescription>Personalize o sistema e seus vouchers.</CardDescription></CardHeader>
            <CardContent>
              <div className="space-y-8 max-w-2xl">
                <div className={`flex items-center gap-6 p-6 border rounded-xl bg-gray-50 ${!isAdmin ? 'opacity-60 grayscale' : ''}`}>
                    <div className="w-24 h-24 bg-white border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden relative shadow-sm">
                        {prefData.logo_url ? <img src={prefData.logo_url} alt="Logo" className="w-full h-full object-contain" /> : <span className="text-xs text-gray-400 text-center px-2">Sem Logo</span>}
                    </div>
                    <div className="flex-1">
                        <Label htmlFor="logo-upload" className="block mb-2 font-medium">Logo da Agência</Label>
                        <p className="text-xs text-gray-500 mb-3">Recomendado: PNG transparente, máx 2MB.</p>
                        <div className="relative">
                            <Input id="logo-upload" type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" disabled={!isAdmin} />
                            <Button asChild variant="outline" disabled={!isAdmin} className="bg-white">
                                <label htmlFor={isAdmin ? "logo-upload" : ""} className={`cursor-pointer flex items-center gap-2 ${!isAdmin ? 'cursor-not-allowed' : ''}`}>
                                    <Upload className="w-4 h-4" /> {isAdmin ? "Fazer Upload" : "Bloqueado"}
                                </label>
                            </Button>
                        </div>
                    </div>
                </div>
                <form onSubmit={handlePrefUpdate} className={`space-y-6 ${!isAdmin ? 'opacity-80 pointer-events-none' : ''}`}>
                    <div>
                        <Label className="mb-2 block">Cor Primária do Sistema</Label>
                        <div className="flex items-center gap-4">
                            <div className="relative overflow-hidden w-16 h-10 rounded-md border shadow-sm">
                                <Input type="color" value={prefData.cor_primaria} onChange={(e) => setPrefData({...prefData, cor_primaria: e.target.value})} className="absolute -top-2 -left-2 w-24 h-24 cursor-pointer p-0 border-0" />
                            </div>
                            <Input value={prefData.cor_primaria} onChange={(e) => setPrefData({...prefData, cor_primaria: e.target.value})} className="w-32 font-mono uppercase" maxLength={7} />
                        </div>
                    </div>
                    <div>
                        <Label className="mb-2 block">Texto Rodapé do Voucher</Label>
                        <textarea className="w-full min-h-[100px] p-3 rounded-md border border-input bg-background text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" value={prefData.texto_voucher} onChange={(e) => setPrefData({...prefData, texto_voucher: e.target.value})} placeholder="Ex: Obrigado pela preferência! Em caso de dúvidas ligue..." />
                    </div>
                    {isAdmin && (<div className="flex justify-end"><Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white"><Save className="w-4 h-4 mr-2" /> Salvar Visual</Button></div>)}
                </form>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* --- EQUIPE (ADMIN) --- */}
        {isAdmin && (
            <TabsContent value="equipe">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-7">
                        <div>
                            <CardTitle>Gestão de Acesso</CardTitle>
                            <CardDescription>Controle quem acessa o painel da agência.</CardDescription>
                        </div>
                        <Button onClick={() => setIsInviteModalOpen(true)} disabled={usageData.atual >= usageData.limite} className={`${usageData.atual >= usageData.limite ? 'opacity-50 cursor-not-allowed' : ''} bg-blue-600 hover:bg-blue-700 text-white`}>
                            <Plus className="w-4 h-4 mr-2" /> {usageData.atual >= usageData.limite ? 'Plano Cheio' : 'Convidar'}
                        </Button>
                    </CardHeader>
                    <CardContent>
                        <div className="mb-6 bg-blue-50/50 p-4 rounded-lg border border-blue-100">
                             <div className="flex justify-between text-sm mb-2">
                                <span className="font-semibold text-gray-700 flex items-center gap-2"><Users className="w-4 h-4"/> Vagas utilizadas</span>
                                <span className={`font-bold ${usageData.atual >= usageData.limite ? 'text-red-600' : 'text-blue-600'}`}>{usageData.atual} de {usageData.limite}</span>
                             </div>
                             <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                                <div className={`h-2.5 rounded-full transition-all duration-500 ${usageData.atual >= usageData.limite ? 'bg-red-500' : 'bg-blue-600'}`} style={{ width: `${Math.min((usageData.atual / usageData.limite) * 100, 100)}%` }}></div>
                             </div>
                             {usageData.atual >= usageData.limite && <p className="text-xs text-red-600 mt-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3"/> Você atingiu o limite do plano. Faça upgrade para adicionar mais pessoas.</p>}
                        </div>

                        <div className="rounded-md border overflow-hidden">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-50 text-gray-600 font-medium border-b"><tr><th className="px-4 py-3">Nome</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Nível</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Ações</th></tr></thead>
                                <tbody className="divide-y">
                                    {teamMembers.map((member) => (
                                        <tr key={member.id} className="hover:bg-gray-50 group">
                                            <td className="px-4 py-3 font-medium text-gray-900">{member.nome}</td>
                                            <td className="px-4 py-3 text-gray-500">{member.email}</td>
                                            <td className="px-4 py-3"><Badge variant="secondary" className="uppercase text-[10px]">{member.role}</Badge></td>
                                            <td className="px-4 py-3">
                                                {/* CORREÇÃO DO STATUS AQUI */}
                                                {member.status === 'Pendente' ? (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">
                                                        Convite Enviado
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                                                        Ativo
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-blue-600" onClick={() => {
                                                        setEditingData({ id: member.vendedor_id || member.id, nome: member.nome, email: member.email, telefone: member.telefone, role: member.role });
                                                        setIsEditModalOpen(true);
                                                    }}><Edit className="w-4 h-4" /></Button>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-red-600" onClick={() => handleDeleteMember(member)}><Trash2 className="w-4 h-4" /></Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            </TabsContent>
        )}


        {/* --- ABA ASSINATURA --- */}
        {isAdmin && (
            <TabsContent value="assinatura" className="space-y-8">
                 
                 {/* CABEÇALHO DA ABA */}
                 <div className="mb-6">
                    <h2 className="text-2xl font-bold tracking-tight text-gray-900">Assinatura e Cobrança</h2>
                    <p className="text-gray-500">Gerencie o plano da sua agência.</p>
                 </div>

                 {/* CARDS DE STATUS (Topo) */}
                 <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    
                    {/* 1. PLANO ATUAL */}
                    <Card className="bg-[#eff6ff] border-[#dbeafe] shadow-sm">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-blue-600">
                                Plano Atual
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-3xl font-bold text-[#1e3a8a]">
                                {subscriptionData?.nome_plano || 'Carregando...'}
                            </div>
                            <div className="mt-1 text-sm font-medium text-blue-600">
                                Limite: {subscriptionData?.limite_usuarios ? `${subscriptionData.limite_usuarios} usuários` : '...'}
                            </div>
                        </CardContent>
                    </Card>

                    {/* 2. STATUS */}
                    <Card className="shadow-sm bg-white border-gray-100">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                Status
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center gap-2 mb-2">
                                {subscriptionData?.status === 'ATIVA' ? (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded text-sm font-semibold bg-green-100 text-green-700">
                                        Ativa
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded text-sm font-semibold bg-red-100 text-red-700">
                                        {subscriptionData?.status || 'Inativo'}
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-gray-500">
                                {subscriptionData?.proxima_cobranca 
                                    ? `Renova em: ${new Date(subscriptionData.proxima_cobranca).toLocaleDateString()}` 
                                    : 'Sem cobrança prevista'}
                            </p>
                        </CardContent>
                    </Card>

                    {/* 3. VALOR MENSAL */}
                    <Card className="shadow-sm bg-white border-gray-100">
                         <CardHeader className="pb-2">
                             <CardTitle className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                 Valor Mensal
                             </CardTitle>
                         </CardHeader>
                         <CardContent>
                             <div className="text-3xl font-bold text-gray-900">
                                 {subscriptionData?.preco_atual === 0 
                                    ? 'Grátis' 
                                    : `R$ ${subscriptionData?.preco_atual?.toFixed(2).replace('.', ',')}`
                                 }
                             </div>
                             <p className="mt-1 text-sm text-gray-500">
                                 Cobrado no cartão de crédito
                             </p>
                         </CardContent>
                    </Card>
                 </div>

                 <div className="border-t border-gray-100 my-8"></div>

                 {/* TÍTULO SEÇÃO PLANOS */}
                 <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                        <Shield className="w-5 h-5 text-gray-900"/>
                        <h3 className="text-lg font-bold text-gray-900">Planos Disponíveis</h3>
                    </div>
                    <span className="text-sm text-gray-500 hidden md:block">
                        Fale conosco para realizar o upgrade
                    </span>
                 </div>
                 
                 {/* GRID DE PLANOS */}
                 <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    
                    {/* === PLANO BÁSICO === */}
                    <Card className={`flex flex-col shadow-sm hover:shadow-md transition-shadow bg-white ${subscriptionData?.plano === 'BASIC' ? 'ring-2 ring-gray-900 bg-gray-50' : ''}`}>
                        <CardHeader>
                            <CardTitle className="text-base font-bold text-gray-900">Plano Básico</CardTitle>
                            <div className="mt-2 flex items-baseline text-gray-900">
                                <span className="text-3xl font-bold tracking-tight">R$ 97</span>
                                <span className="ml-1 text-sm text-gray-500">/mês</span>
                            </div>
                        </CardHeader>
                        <CardContent className="flex-1 space-y-3">
                             <div className="flex items-center gap-3"><Check className="w-4 h-4 text-green-500 flex-shrink-0"/> <span className="text-sm text-gray-600">Até 3 Usuários</span></div>
                             <div className="flex items-center gap-3"><Check className="w-4 h-4 text-green-500 flex-shrink-0"/> <span className="text-sm text-gray-600">Gestão de Vendas</span></div>
                             <div className="flex items-center gap-3"><Check className="w-4 h-4 text-green-500 flex-shrink-0"/> <span className="text-sm text-gray-600">Suporte Email</span></div>
                        </CardContent>
                        <CardFooter>
                            <Button 
                                className="w-full gap-2" 
                                variant={subscriptionData?.plano === 'BASIC' ? "outline" : "secondary"}
                                disabled={subscriptionData?.plano === 'BASIC'} 
                                onClick={()=>handleContactUpgrade('Plano Básico')}
                            >
                                {subscriptionData?.plano === 'BASIC' ? 'Plano Atual' : (
                                    <>
                                        <MessageSquare className="w-4 h-4" /> Falar com Consultor
                                    </>
                                )}
                            </Button>
                        </CardFooter>
                    </Card>

                    {/* === PLANO PROFISSIONAL (DESTAQUE) === */}
                    <Card className={`flex flex-col shadow-md hover:shadow-lg transition-shadow relative bg-white border-blue-100 ${subscriptionData?.plano === 'PRO' ? 'ring-2 ring-blue-600 bg-blue-50/20' : ''}`}>
                        <div className="absolute top-4 right-4">
                             <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                Popular
                             </span>
                        </div>
                        
                        <CardHeader>
                            <CardTitle className="text-base font-bold text-gray-900">Plano Profissional</CardTitle>
                            <div className="mt-2 flex items-baseline text-gray-900">
                                <span className="text-3xl font-bold tracking-tight">R$ 197</span>
                                <span className="ml-1 text-sm text-gray-500">/mês</span>
                            </div>
                        </CardHeader>
                        <CardContent className="flex-1 space-y-3">
                             <div className="flex items-center gap-3"><Check className="w-4 h-4 text-green-500 flex-shrink-0"/> <span className="text-sm text-gray-600">Até 10 Usuários</span></div>
                             <div className="flex items-center gap-3"><Check className="w-4 h-4 text-green-500 flex-shrink-0"/> <span className="text-sm text-gray-600 font-medium">Tudo do Básico</span></div>
                             <div className="flex items-center gap-3"><Check className="w-4 h-4 text-green-500 flex-shrink-0"/> <span className="text-sm text-gray-600">Relatórios Avançados</span></div>
                             <div className="flex items-center gap-3"><Check className="w-4 h-4 text-green-500 flex-shrink-0"/> <span className="text-sm text-gray-600">Suporte Prioritário</span></div>
                        </CardContent>
                        <CardFooter>
                            <Button 
                                className={`w-full gap-2 ${subscriptionData?.plano === 'PRO' ? '' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                                variant={subscriptionData?.plano === 'PRO' ? "outline" : "default"}
                                disabled={subscriptionData?.plano === 'PRO'} 
                                onClick={()=>handleContactUpgrade('Plano Profissional')}
                            >
                                {subscriptionData?.plano === 'PRO' ? 'Plano Atual' : (
                                    <>
                                        <MessageSquare className="w-4 h-4" /> Solicitar Upgrade
                                    </>
                                )}
                            </Button>
                        </CardFooter>
                    </Card>

                    {/* === ENTERPRISE === */}
                    <Card className={`flex flex-col shadow-sm hover:shadow-md transition-shadow bg-white ${subscriptionData?.plano === 'ENTERPRISE' ? 'ring-2 ring-gray-900' : ''}`}>
                        <CardHeader>
                            <CardTitle className="text-base font-bold text-gray-900">Enterprise</CardTitle>
                            <div className="mt-2 flex items-baseline text-gray-900">
                                <span className="text-3xl font-bold tracking-tight">R$ 497</span>
                                <span className="ml-1 text-sm text-gray-500">/mês</span>
                            </div>
                        </CardHeader>
                         <CardContent className="flex-1 space-y-3">
                             <div className="flex items-center gap-3"><Check className="w-4 h-4 text-green-500 flex-shrink-0"/> <span className="text-sm text-gray-600">Usuários Ilimitados</span></div>
                             <div className="flex items-center gap-3"><Check className="w-4 h-4 text-green-500 flex-shrink-0"/> <span className="text-sm text-gray-600">White Label Total</span></div>
                             <div className="flex items-center gap-3"><Check className="w-4 h-4 text-green-500 flex-shrink-0"/> <span className="text-sm text-gray-600">Gerente de Conta</span></div>
                         </CardContent>
                        <CardFooter>
                            <Button 
                                variant="outline" 
                                className="w-full border-gray-200 text-gray-900 hover:bg-gray-50 gap-2" 
                                disabled={subscriptionData?.plano === 'ENTERPRISE'} 
                                onClick={()=>handleContactUpgrade('Plano Enterprise')}
                            >
                                {subscriptionData?.plano === 'ENTERPRISE' ? 'Plano Atual' : (
                                    <>
                                        <ExternalLink className="w-4 h-4" /> Falar com Vendas
                                    </>
                                )}
                            </Button>
                        </CardFooter>
                    </Card>
                 </div>

                 {/* RODAPÉ DE AJUDA FINANCEIRA */}
                 <div className="mt-8 text-center bg-gray-50 p-6 rounded-lg border border-dashed border-gray-300">
                    <p className="text-gray-600 text-sm">
                        Precisa de um plano customizado ou tem dúvidas sobre sua fatura? <br/>
                        Entre em contato direto com nosso financeiro através do email <span className="font-medium text-gray-900">financeiro@operya.com.br</span>
                    </p>
                 </div>
            </TabsContent>
        )}
      </Tabs>


      {/* MODAL CONVITE (COM DIALOG CORRIGIDO) */}
      <Dialog open={isInviteModalOpen} onOpenChange={setIsInviteModalOpen}>
        <DialogContent>
            <DialogHeader><DialogTitle>Convidar Novo Membro</DialogTitle></DialogHeader>
            <form onSubmit={handleInviteMember} className="space-y-4 pt-2">
                <div><Label>Nome</Label><Input value={inviteData.nome} onChange={(e) => setInviteData({...inviteData, nome: e.target.value})} required /></div>
                <div><Label>Email</Label><Input type="email" value={inviteData.email} onChange={(e) => setInviteData({...inviteData, email: e.target.value})} required /></div>
                <div><Label>Acesso</Label><Select value={inviteData.role} onValueChange={(v) => setInviteData({...inviteData, role: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="vendedor">Vendedor</SelectItem><SelectItem value="financeiro">Financeiro</SelectItem><SelectItem value="admin">Administrador</SelectItem></SelectContent></Select></div>
                <DialogFooter><Button type="button" variant="outline" onClick={() => setIsInviteModalOpen(false)}>Cancelar</Button><Button type="submit" disabled={loading} className="bg-blue-600 text-white">{loading ? 'Enviando...' : 'Enviar'}</Button></DialogFooter>
            </form>
        </DialogContent>
      </Dialog>

      {/* MODAL EDIÇÃO */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent>
            <DialogHeader><DialogTitle>Editar Membro</DialogTitle></DialogHeader>
            <form onSubmit={handleSaveEdit} className="space-y-4 pt-2">
                <div><Label>Nome</Label><Input value={editingData.nome} onChange={(e) => setEditingData({...editingData, nome: e.target.value})} required /></div>
                <div><Label>Email</Label><Input type="email" value={editingData.email} onChange={(e) => setEditingData({...editingData, email: e.target.value})} required /></div>
                <div><Label>Telefone</Label><Input value={editingData.telefone} onChange={(e) => setEditingData({...editingData, telefone: maskPhone(e.target.value)})} maxLength={15} /></div>
                <div><Label>Acesso</Label><Select value={editingData.role} onValueChange={(v) => setEditingData({...editingData, role: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="vendedor">Vendedor</SelectItem><SelectItem value="financeiro">Financeiro</SelectItem><SelectItem value="comercial">Comercial</SelectItem><SelectItem value="marketing">Marketing</SelectItem><SelectItem value="admin">Administrador</SelectItem></SelectContent></Select></div>
                <DialogFooter><Button type="button" variant="outline" onClick={() => setIsEditModalOpen(false)}>Cancelar</Button><Button type="submit" disabled={loading} className="bg-blue-600 text-white">{loading ? 'Salvando...' : 'Salvar'}</Button></DialogFooter>
            </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}